import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, throwError } from 'rxjs';
import {
  ApiResponse, JwtAuthResponse, LoginRequest, ForgotPasswordRequest,
  ValidateOtpRequest, ChangePasswordRequest, UserRank, SlaAgreementResponse,
  MfaVerifyRequest, ResendMfaRequest, RefreshTokenRequest
} from '../models/models';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/auth`;
  private readonly userApiUrl = `${environment.apiUrl}/user`;
  private readonly slaApiUrl = `${environment.apiV1Url}/sla`;

  currentUser = signal<JwtAuthResponse | null>(this.getStoredUser());
  token = signal<string | null>(localStorage.getItem(environment.auth.tokenKey));
  refreshToken = signal<string | null>(localStorage.getItem(environment.auth.refreshTokenKey));

  // Computed role & rank helpers
  isLoggedIn = computed(() => !!this.token() && !!this.currentUser());

  userName = computed(() => {
    const usr = this.currentUser();
    return usr?.fullName || usr?.email || 'User';
  });

  userRoles = computed(() => {
    const usr = this.currentUser();
    return usr?.roles || [];
  });

  primaryRole = computed(() => {
    const roles = this.userRoles();
    return roles.length > 0 ? roles[0] : 'User';
  });

  userRank = computed(() => {
    return this.currentUser()?.rank;
  });

  institutionName = computed(() => {
    return this.currentUser()?.institutionName || 'Zanzibar Government Authority';
  });

  institutionUid = computed(() => {
    return this.currentUser()?.institutionUid;
  });

  hasInstitution = computed(() => {
    const usr = this.currentUser();
    if (!usr) return false;
    if (this.isInternal()) return false;
    return !!usr.institutionUid || this.isInstitutionAdmin();
  });

  isSlaAccepted = computed(() => {
    const usr = this.currentUser();
    if (!usr) return true;
    if (this.isInternal()) return true;
    return Boolean(usr.slaAccepted);
  });

  slaAcceptedAt = computed(() => this.currentUser()?.slaAcceptedAt);
  slaAcceptedBy = computed(() => this.currentUser()?.slaAcceptedBy);

  canManageOnboarding = computed(() => {
    return this.hasRole('SuperAdmin') ||
           this.isInternal() ||
           this.hasPermission('can approve institution onboarding') ||
           this.hasPermission('can return institution onboarding') ||
           this.hasPermission('can reject institution onboarding') ||
           this.hasPermission('can view all onboarding applications');
  });

  userPermissions = computed(() => {
    const usr = this.currentUser();
    const explicitPerms = usr?.permissions || [];
    const perms = new Set<string>(explicitPerms.map(p => p.toLowerCase().trim()));

    if (this.hasRole('SuperAdmin')) {
      [
        'can assign role', 'can approve data exchange request', 'can view user role',
        'can manage services', 'can manage workflows',
        'can view all onboarding applications', 'can view onboarding application',
        'can approve institution onboarding', 'can reject institution onboarding', 'can return institution onboarding',
        'can register onboarding institution', 'can update institution status', 'can create onboarding admin user'
      ].forEach(p => perms.add(p.toLowerCase().trim()));
    } else if (this.hasRole('InstitutionAdmin') || this.userRank() === UserRank.INSTITUTION_ADMIN) {
      ['can manage services', 'can view user role', 'can approve data exchange request', 'can assign role'].forEach(p => perms.add(p.toLowerCase().trim()));
    } else if (this.hasRole('Reviewer')) {
      ['can approve data exchange request'].forEach(p => perms.add(p.toLowerCase().trim()));
    }

    return Array.from(perms);
  });

  constructor(private http: HttpClient) {}

  private getStoredUser(): JwtAuthResponse | null {
    const u = localStorage.getItem(environment.auth.userKey);
    return u ? JSON.parse(u) : null;
  }

  handleAuthSuccess(data: JwtAuthResponse) {
    if (data.token) {
      this.token.set(data.token);
      localStorage.setItem(environment.auth.tokenKey, data.token);
    }
    if (data.refreshToken) {
      this.refreshToken.set(data.refreshToken);
      localStorage.setItem(environment.auth.refreshTokenKey, data.refreshToken);
    }
    this.currentUser.set(data);
    localStorage.setItem(environment.auth.userKey, JSON.stringify(data));
  }

  login(request: LoginRequest): Observable<ApiResponse<JwtAuthResponse>> {
    return this.http.post<ApiResponse<JwtAuthResponse>>(`${this.apiUrl}/login`, request).pipe(
      tap(res => {
        if (res.success && res.data && !res.data.mfaRequired) {
          this.handleAuthSuccess(res.data);
        }
      })
    );
  }

  verifyMfa(request: MfaVerifyRequest): Observable<ApiResponse<JwtAuthResponse>> {
    return this.http.post<ApiResponse<JwtAuthResponse>>(`${this.apiUrl}/verify-mfa`, request).pipe(
      tap(res => {
        if (res.success && res.data) {
          this.handleAuthSuccess(res.data);
        }
      })
    );
  }

  resendMfa(request: ResendMfaRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/resend-mfa`, request);
  }

  refreshAccessToken(): Observable<ApiResponse<JwtAuthResponse>> {
    const rfToken = this.refreshToken();
    if (!rfToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<ApiResponse<JwtAuthResponse>>(`${this.apiUrl}/refresh-token`, { refreshToken: rfToken }).pipe(
      tap(res => {
        if (res.success && res.data) {
          this.handleAuthSuccess(res.data);
        }
      }),
      catchError(err => {
        // If refresh failed, clear credentials
        this.logout();
        return throwError(() => err);
      })
    );
  }

  forgotPassword(request: ForgotPasswordRequest): Observable<ApiResponse<String>> {
    return this.http.post<ApiResponse<String>>(`${this.apiUrl}/forgot_password`, request);
  }

  validateOtp(request: ValidateOtpRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/validate_otp`, request);
  }

  changePassword(request: ChangePasswordRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/change_password`, request);
  }

  acceptSla(): Observable<ApiResponse<SlaAgreementResponse>> {
    return this.http.post<ApiResponse<SlaAgreementResponse>>(`${this.slaApiUrl}/accept`, {}).pipe(
      tap(res => {
        if (res.success && res.data) {
          const usr = this.currentUser();
          if (usr) {
            const updated: JwtAuthResponse = {
              ...usr,
              slaAccepted: true,
              slaAcceptedAt: res.data.slaAcceptedAt,
              slaAcceptedBy: res.data.slaAcceptedBy
            };
            this.currentUser.set(updated);
            localStorage.setItem(environment.auth.userKey, JSON.stringify(updated));
          }
        }
      })
    );
  }

  getSlaStatus(): Observable<ApiResponse<SlaAgreementResponse>> {
    return this.http.get<ApiResponse<SlaAgreementResponse>>(`${this.slaApiUrl}/institution-status`);
  }

  logout() {
    const rfToken = this.refreshToken();
    if (rfToken) {
      this.http.post(`${this.apiUrl}/logout`, { refreshToken: rfToken }).subscribe({
        next: () => {},
        error: () => {}
      });
    }

    this.token.set(null);
    this.refreshToken.set(null);
    this.currentUser.set(null);
    localStorage.removeItem(environment.auth.tokenKey);
    localStorage.removeItem(environment.auth.refreshTokenKey);
    localStorage.removeItem(environment.auth.userKey);
    localStorage.removeItem(environment.auth.personaKey);
  }

  // RBAC helper methods
  hasRole(role: string): boolean {
    if (!role) return false;
    const target = role.toLowerCase().replace(/^role_/, '').replace(/[\s_-]+/g, '');
    return this.userRoles().some(r => {
      const normalized = (r || '').toLowerCase().replace(/^role_/, '').replace(/[\s_-]+/g, '');
      return normalized === target;
    });
  }

  hasPermission(permission: string): boolean {
    if (!permission) return false;
    if (this.hasRole('SuperAdmin')) return true;
    const target = permission.toLowerCase().trim();
    return this.userPermissions().includes(target);
  }

  isInternal(): boolean {
    return this.userRank() === UserRank.INTERNAL;
  }

  isInstitutionAdmin(): boolean {
    return this.userRank() === UserRank.INSTITUTION_ADMIN || this.hasRole('InstitutionAdmin');
  }
}
